import type { StudiesLobbyDraftItem } from '@/types/routes/tests'
import i18n from '@/i18n'

/**
 * Display mapping for questionnaire-draft lobby items.
 *
 * Lifecycle filtering (which drafts are visible, launched/archived exclusion)
 * is owned by the backend. This module only maps the raw draft `status` into
 * lobby display copy and never decides visibility.
 */

/** Raw draft statuses that mean the draft is ready for a final review pass. */
const READY_TO_REVIEW_STATUSES = new Set<string>(['ready_for_review'])

/** In-progress statuses the lobby can name. Unknown statuses stay "Draft". */
function stageStatusLabel(status: string): string | null {
  switch (status) {
    case 'collecting_problem':
      return i18n.t('studies.draftStatus.collectingProblem', { ns: 'app' })
    case 'asking_clarifying_questions':
      return i18n.t('studies.draftStatus.askingQuestions', { ns: 'app' })
    case 'draft_ready':
      return i18n.t('studies.draftStatus.readyToBuild', { ns: 'app' })
    case 'editing':
      return i18n.t('studies.draftStatus.editing', { ns: 'app' })
    case 'assets_required':
      return i18n.t('studies.draftStatus.needsAssets', { ns: 'app' })
    default:
      return null
  }
}

export interface DraftDisplayCopy {
  /** Badge / status column copy. */
  statusLabel: string
  /** Primary action label (open the draft). */
  actionLabel: string
  isReadyToReview: boolean
}

export function getDraftDisplayCopy(status: string): DraftDisplayCopy {
  if (status === 'duplicating') {
    return {
      statusLabel: i18n.t('studies.draftStatus.duplicating', { ns: 'app' }),
      actionLabel: i18n.t('studies.draftAction.duplicating', { ns: 'app' }),
      isReadyToReview: false,
    }
  }
  if (status === 'duplicate_failed') {
    return {
      statusLabel: i18n.t('studies.draftStatus.duplicateFailed', { ns: 'app' }),
      actionLabel: i18n.t('studies.draftAction.duplicateFailed', { ns: 'app' }),
      isReadyToReview: false,
    }
  }
  const isReadyToReview = READY_TO_REVIEW_STATUSES.has(status)
  const stageLabel = stageStatusLabel(status)
  return {
    statusLabel: isReadyToReview
      ? i18n.t('studies.draftStatus.ready', { ns: 'app' })
      : stageLabel
        ? stageLabel
        : i18n.t('studies.draftStatus.draft', { ns: 'app' }),
    actionLabel: isReadyToReview
      ? i18n.t('studies.draftAction.review', { ns: 'app' })
      : i18n.t('studies.draftAction.continue', { ns: 'app' }),
    isReadyToReview,
  }
}

export function getDraftDisplayName(draft: StudiesLobbyDraftItem): string {
  const displayName = draft.displayName?.trim()
  if (displayName) return displayName
  const title = draft.title?.trim()
  return title || i18n.t('studies.untitledDraft', { ns: 'app' })
}
